/**
 * Persona spin-up helpers.
 *
 * A persona is a `Person` with `category: 'persona'` plus the
 * `PersonaExtension` fields (`archetype`, `summary`). They are ICP
 * archetypes used to condition the Content Engine and scoring. This
 * module supplies the starter blueprints the Personas tab offers
 * ("spin up from a template") and a synthesizer that drafts a persona
 * grounded in the real prospects/customers already in the CRM.
 */

import type { Person, PersonaProfile } from '../db/types';

// ---------------------------------------------------------------------------
// Purpose copy + downstream feeds — the "what is this for?" answer.
// Exported so the Personas tab (and any other surface) renders the exact
// same story, and so tests can pin that the copy names every consumer.
// ---------------------------------------------------------------------------

/**
 * The one-paragraph purpose statement shown at the top of the Personas tab.
 * Personas are not contacts — they are the buyer profiles the rest of the
 * app tunes itself to.
 */
export const PERSONA_PURPOSE =
  'Personas are the buyer profiles Argo tunes itself to. Define who you ' +
  'sell to — their goals, pains, and objections — once, and every draft, ' +
  'sequence, and score downstream speaks to that person instead of a ' +
  'generic "prospect".';

/** One downstream consumer a persona conditions. */
export interface PersonaFeed {
  id: 'content' | 'outreach' | 'scoring';
  /** Short chip label. */
  label: string;
  /** One-liner explaining exactly how the persona is used there. */
  detail: string;
}

/**
 * Where a persona actually feeds — each entry is a real consumer in the
 * app today, not aspiration: the Content Engine has a persona picker for
 * drafts, outreach/sequence drafting leans on objections + channels, and
 * win/loss reports conversion by persona archetype.
 */
export const PERSONA_FEEDS: PersonaFeed[] = [
  {
    id: 'content',
    label: 'Content Engine',
    detail: 'Drafts are tailored to the selected persona — its goals, pains, and voice.',
  },
  {
    id: 'outreach',
    label: 'Outreach & sequences',
    detail: 'Objections, channels, and seniority tune the angle and tone of every touch.',
  },
  {
    id: 'scoring',
    label: 'Scoring & win/loss',
    detail: 'ICP fit scoring and win-rate-by-archetype reporting key off your personas.',
  },
];

/** Curated option lists for the structured persona editor. */
export const SENIORITY_OPTIONS = [
  '',
  'C-Suite',
  'VP',
  'Director',
  'Manager',
  'Individual Contributor',
];

export const COMPANY_SIZE_OPTIONS = [
  '',
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+',
];

/** A starter blueprint shown in the spin-up modal. */
export interface PersonaTemplate {
  id: string;
  /** Card / option label. */
  label: string;
  /** Stored on `persona.archetype`. */
  archetype: string;
  /** One-liner on the template card. */
  blurb: string;
  /** Pre-filled `persona.summary`. */
  summary: string;
  tags: string[];
  /** Pre-filled structured ICP definition. */
  profile: PersonaProfile;
  /**
   * Picker grouping: `role` templates describe WHO the buyer is (the
   * "Agency Owner" shape), `buying-role` templates describe the seat they
   * hold in the buying committee, `custom` is the blank slate.
   */
  group: 'role' | 'buying-role' | 'custom';
}

export const PERSONA_TEMPLATES: PersonaTemplate[] = [
  // --- Role starters: who the buyer actually is -------------------------
  {
    id: 'agency-owner',
    label: 'Agency Owner',
    archetype: 'Agency Owner',
    blurb: 'Runs a services shop; buys leverage, not tools.',
    summary:
      'Owner-operator of a small agency wearing every hat — sales, delivery, ' +
      'hiring. Time-poor and margin-obsessed. Buys anything that wins clients ' +
      'or claws back billable hours, but is allergic to setup projects and ' +
      'per-seat pricing that scales against them.',
    tags: ['icp', 'agency', 'owner'],
    group: 'role',
    profile: {
      role: 'Founder / Owner',
      seniority: 'C-Suite',
      companySize: '1-10',
      industry: 'Marketing / Creative agency',
      goals: [
        'Win and keep retainer clients',
        'Grow margin without growing headcount',
        'Get out of day-to-day delivery',
      ],
      painPoints: [
        'Everything routes through the owner',
        'Feast-or-famine pipeline',
        'Client reporting eats billable hours',
      ],
      motivations: [
        'More revenue per head',
        'Predictable months',
        'Freedom from the tool-and-tab circus',
      ],
      objections: [
        'I do not have time to set another thing up',
        'Will this actually win me clients?',
        'Per-seat pricing punishes my growth',
      ],
      decisionCriteria: [
        'Time-to-first-win under a week',
        'Flat, predictable pricing',
        'Looks great in front of clients',
      ],
      channels: ['Referrals / peer groups', 'LinkedIn', 'Podcasts and communities'],
      kpis: ['Retainer revenue', 'Margin per client', 'Owner hours in delivery'],
      quote: 'If it does not win me a client or save me a night, I am out.',
    },
  },
  {
    id: 'revops-lead',
    label: 'RevOps Lead',
    archetype: 'RevOps Lead',
    blurb: 'Owns the CRM, the data, and the process.',
    summary:
      'Owns the revenue stack end-to-end: CRM hygiene, pipeline reporting, ' +
      'and the handoffs between marketing, sales, and CS. Evaluates every ' +
      'tool on data quality, integration surface, and whether reps will ' +
      'actually use it.',
    tags: ['icp', 'revops', 'operations'],
    group: 'role',
    profile: {
      role: 'RevOps / Sales Ops lead',
      seniority: 'Director',
      companySize: '51-200',
      industry: 'B2B SaaS',
      goals: [
        'One trustworthy source of truth',
        'Forecasts leadership believes',
        'Fewer tools, better adopted',
      ],
      painPoints: [
        'Dirty, duplicated CRM data',
        'Reps who will not log activity',
        'Reporting stitched from five exports',
      ],
      motivations: [
        'Being the person with the real numbers',
        'Process that runs without babysitting',
        'Stack consolidation wins',
      ],
      objections: [
        'How does this stay in sync with our CRM?',
        'Will reps actually adopt it?',
        'Is this another data silo?',
      ],
      decisionCriteria: [
        'Data model and integration quality',
        'Adoption friction for reps',
        'Auditability of every change',
      ],
      channels: ['RevOps communities', 'LinkedIn', 'Vendor comparison content'],
      kpis: ['Pipeline accuracy', 'CRM hygiene score', 'Forecast variance'],
      quote: 'If the data is wrong, everything downstream is theater.',
    },
  },
  {
    id: 'marketing-director',
    label: 'Marketing Director',
    archetype: 'Marketing Director',
    blurb: 'Owns pipeline targets and the content machine.',
    summary:
      'Carries a pipeline number and runs the content/demand machine that ' +
      'feeds it. Stretched across channels with a small team; buys anything ' +
      'that ships more quality output per head and proves its contribution ' +
      'to pipeline.',
    tags: ['icp', 'marketing', 'demand-gen'],
    group: 'role',
    profile: {
      role: 'Director of Marketing',
      seniority: 'Director',
      companySize: '51-200',
      industry: 'B2B SaaS / Services',
      goals: [
        'Hit the pipeline contribution target',
        'Ship more content without more headcount',
        'Prove marketing ROI to the CEO',
      ],
      painPoints: [
        'Content bottlenecked on one writer',
        'Attribution nobody trusts',
        'Every channel is somebody\u2019s side job',
      ],
      motivations: [
        'Visible pipeline wins',
        'A machine that compounds',
        'Credibility with sales',
      ],
      objections: [
        'Will the output actually sound like us?',
        'How is this different from a generic AI writer?',
        'Can I tie it to pipeline?',
      ],
      decisionCriteria: [
        'Output quality on-brand',
        'Speed from idea to published',
        'Reporting that ties to revenue',
      ],
      channels: ['LinkedIn', 'Marketing newsletters', 'Peer recommendations'],
      kpis: ['Pipeline sourced', 'Content velocity', 'Conversion rates'],
      quote: 'I need more shots on goal without hiring three more people.',
    },
  },
  {
    id: 'founder-ceo',
    label: 'Founder / CEO',
    archetype: 'Founder / CEO',
    blurb: 'Sells, builds, and decides — often same day.',
    summary:
      'Founder still doing founder-led sales. Decides fast, churns fast. ' +
      'Cares about momentum: anything that shortens the path from ' +
      'conversation to closed revenue earns a spot; anything that adds ' +
      'process for its own sake gets cut.',
    tags: ['icp', 'founder', 'smb'],
    group: 'role',
    profile: {
      role: 'Founder / CEO',
      seniority: 'C-Suite',
      companySize: '1-10',
      industry: 'Startup / SMB',
      goals: [
        'Close the next ten customers',
        'Find repeatable go-to-market motion',
        'Stay alive to the next milestone',
      ],
      painPoints: [
        'Sales pipeline lives in their head',
        'No time for tooling or process',
        'Follow-ups slip and deals go cold',
      ],
      motivations: ['Speed and momentum', 'Doing more with a tiny team', 'Survival'],
      objections: [
        'I can do this in a spreadsheet',
        'Is this worth my time to learn?',
        'What if we pivot next quarter?',
      ],
      decisionCriteria: [
        'Value in the first session',
        'Cancel-anytime simplicity',
        'Zero-admin adoption',
      ],
      channels: ['Founder communities', 'Twitter/X', 'Word of mouth'],
      kpis: ['New revenue', 'Runway', 'Deals in motion'],
      quote: 'Show me it works in the first hour or I am back to my spreadsheet.',
    },
  },

  // --- Buying-committee seats: the role they play in the deal -----------
  {
    id: 'economic-buyer',
    label: 'Economic Buyer',
    archetype: 'Economic Buyer',
    blurb: 'Owns the budget and the business case.',
    group: 'buying-role',
    summary:
      'Senior decision-maker who controls the budget and signs off on spend. ' +
      'Cares about ROI, payback period, and risk. Wants a tight business case ' +
      'and clear proof of outcome before committing.',
    tags: ['icp', 'buyer', 'decision-maker'],
    profile: {
      role: 'VP / Head of function',
      seniority: 'VP',
      companySize: '51-200',
      industry: 'B2B SaaS / Services',
      goals: [
        'Hit revenue and efficiency targets',
        'De-risk the next budget cycle',
        'Show measurable ROI to leadership',
      ],
      painPoints: [
        'Spend that does not tie to outcomes',
        'Long, uncertain payback periods',
        'Tool sprawl with no clear owner',
      ],
      motivations: [
        'Predictable return on every dollar',
        'A competitive edge',
        'Looking decisive to the board',
      ],
      objections: [
        'How fast do we see payback?',
        'What happens if it does not work?',
        'Why now versus next quarter?',
      ],
      decisionCriteria: [
        'Clear business case and ROI model',
        'Reference customers in our space',
        'Low switching / implementation risk',
      ],
      channels: ['Warm intro / referral', 'Executive briefing', 'Peer recommendation'],
      kpis: ['Revenue / pipeline', 'Cost-to-serve', 'Payback period'],
      quote: 'Show me the business case and the payback, then we can talk.',
    },
  },
  {
    id: 'champion',
    label: 'Internal Champion',
    archetype: 'Champion',
    blurb: 'Advocates internally and drives adoption.',
    group: 'buying-role',
    summary:
      'Hands-on operator who feels the pain daily and pushes the deal from the ' +
      'inside. Needs ammunition to sell up the chain and a quick, visible win ' +
      'to point to.',
    tags: ['icp', 'champion', 'adoption'],
    profile: {
      role: 'Team lead / Senior operator',
      seniority: 'Manager',
      companySize: '51-200',
      industry: 'B2B SaaS / Services',
      goals: [
        'Fix the daily pain for the team',
        'Earn a visible, quick win',
        'Get buy-in from leadership',
      ],
      painPoints: [
        'Manual, repetitive work',
        'No air-cover to push change',
        'Being blamed when tools fail',
      ],
      motivations: [
        'Make the team faster',
        'Career growth and recognition',
        'Be the person who fixed it',
      ],
      objections: [
        'Will leadership fund this?',
        'How hard is rollout for my team?',
        'What if adoption stalls?',
      ],
      decisionCriteria: [
        'Quick, visible time-to-value',
        'Easy onboarding',
        'Responsive support',
      ],
      channels: ['Product trial / demo', 'Community / Slack', 'Content and how-tos'],
      kpis: ['Team throughput', 'Adoption / usage', 'Cycle time'],
      quote: 'If it saves my team hours a week, I will champion it upstairs.',
    },
  },
  {
    id: 'technical-evaluator',
    label: 'Technical Evaluator',
    archetype: 'Technical Evaluator',
    blurb: 'Vets security, integration, and fit.',
    group: 'buying-role',
    summary:
      'Engineer or ops lead who validates integration, security, and data ' +
      'handling. Wants documentation, a sandbox, and clear compliance answers ' +
      'before greenlighting.',
    tags: ['icp', 'technical', 'security'],
    profile: {
      role: 'Engineering / Ops lead',
      seniority: 'Director',
      companySize: '201-1000',
      industry: 'Technology',
      goals: [
        'Ship without adding risk',
        'Keep the stack secure and compliant',
        'Avoid integration debt',
      ],
      painPoints: [
        'Black-box tools',
        'Security and compliance gaps',
        'Brittle integrations',
      ],
      motivations: ['Reliability and control', 'Clean architecture', 'Fewer 2am pages'],
      objections: [
        'How do you handle our data?',
        'What is the integration surface?',
        'Is there a sandbox?',
      ],
      decisionCriteria: [
        'Security and compliance posture',
        'API / integration quality',
        'Documentation and sandbox access',
      ],
      channels: ['Docs and technical content', 'Sandbox / POC', 'Engineer-to-engineer'],
      kpis: ['Uptime / reliability', 'Integration effort', 'Security incidents'],
      quote: 'Give me the docs, a sandbox, and your security posture — then I will sign off.',
    },
  },
  {
    id: 'end-user',
    label: 'End User',
    archetype: 'End User',
    blurb: 'Lives in the product day to day.',
    group: 'buying-role',
    summary:
      'Front-line user whose workflow the product touches directly. Cares about ' +
      'ease of use and time saved; resists tools that add friction to the day.',
    tags: ['icp', 'user', 'workflow'],
    profile: {
      role: 'Front-line user',
      seniority: 'Individual Contributor',
      companySize: '51-200',
      industry: 'B2B SaaS / Services',
      goals: ['Get the job done faster', 'Less busywork', 'Look good at work'],
      painPoints: [
        'Clunky tools that add steps',
        'Constant context switching',
        'Steep learning curves',
      ],
      motivations: ['Save time', 'Less day-to-day frustration', 'Do meaningful work'],
      objections: [
        'Is this just more overhead?',
        'Do I have to change my workflow?',
        'Will it actually be faster?',
      ],
      decisionCriteria: [
        'Ease of use',
        'Fits the existing workflow',
        'Minimal training',
      ],
      channels: ['In-product', 'Peer word-of-mouth', 'Short videos'],
      kpis: ['Time saved', 'Task completion', 'Satisfaction'],
      quote: 'If it makes my day easier without extra steps, I am in.',
    },
  },
  {
    id: 'custom',
    label: 'Custom persona',
    archetype: '',
    blurb: 'Start from a blank archetype.',
    group: 'custom',
    summary: '',
    tags: ['icp'],
    profile: {},
  },
];

const TEMPLATE_BY_ID: Record<string, PersonaTemplate> = Object.fromEntries(
  PERSONA_TEMPLATES.map((t) => [t.id, t]),
);

export function personaTemplate(id: string): PersonaTemplate {
  return TEMPLATE_BY_ID[id] ?? TEMPLATE_BY_ID.custom;
}

/** The draft fields a spin-up produces. */
export interface PersonaDraft {
  name: string;
  archetype: string;
  summary: string;
  tags: string[];
  profile: PersonaProfile;
}

// ---------------------------------------------------------------------------
// Validation + completeness — pure helpers the spin-up modal and cards use.
// ---------------------------------------------------------------------------

/** A blocking problem with a persona draft, tied to the field at fault. */
export interface PersonaValidationIssue {
  field: 'name' | 'summary';
  message: string;
}

/**
 * Validate a persona draft before creation. Pure and deliberately light:
 * only two things genuinely block a useful persona —
 *
 *   1. a name (it labels the persona everywhere it feeds: content drafts,
 *      sequences, win/loss reporting), which must also be unique among
 *      `existingNames` so two personas can't collide in those pickers;
 *   2. SOME identity signal — an archetype or a summary — otherwise the
 *      downstream consumers have literally nothing to tune to.
 *
 * Everything else (goals, objections, …) is optional richness; the
 * completeness meter nudges toward it without blocking.
 */
export function validatePersonaDraft(
  draft: Pick<PersonaDraft, 'name' | 'archetype' | 'summary'>,
  existingNames: string[] = [],
): PersonaValidationIssue[] {
  const issues: PersonaValidationIssue[] = [];
  const name = draft.name.trim();
  if (!name) {
    issues.push({
      field: 'name',
      message: 'Give the persona a name — it labels drafts, sequences, and reports.',
    });
  } else {
    const taken = existingNames.some(
      (n) => n.trim().toLowerCase() === name.toLowerCase(),
    );
    if (taken) {
      issues.push({
        field: 'name',
        message: `A persona named “${name}” already exists — pick a distinct name.`,
      });
    }
  }
  if (!draft.archetype.trim() && !draft.summary.trim()) {
    issues.push({
      field: 'summary',
      message:
        'Add an archetype or a one-line summary so content and outreach have something to tune to.',
    });
  }
  return issues;
}

/** The structured-profile dimensions the completeness meter counts. */
export const PROFILE_DIMENSIONS: { key: keyof PersonaProfile; label: string }[] = [
  { key: 'role', label: 'Role' },
  { key: 'seniority', label: 'Seniority' },
  { key: 'companySize', label: 'Company size' },
  { key: 'industry', label: 'Industry' },
  { key: 'goals', label: 'Goals' },
  { key: 'painPoints', label: 'Pain points' },
  { key: 'motivations', label: 'Motivations' },
  { key: 'objections', label: 'Objections' },
  { key: 'decisionCriteria', label: 'Decision criteria' },
  { key: 'channels', label: 'Channels' },
  { key: 'kpis', label: 'KPIs' },
  { key: 'quote', label: 'Quote' },
];

/** How filled-in a structured persona profile is. */
export interface PersonaCompleteness {
  /** Dimensions with at least one non-blank value. */
  filled: number;
  /** Total dimensions counted (PROFILE_DIMENSIONS.length). */
  total: number;
  /** filled/total as an integer percentage 0–100. */
  pct: number;
  /** Labels of the empty dimensions, in canonical order. */
  missing: string[];
}

/**
 * Score how complete a structured profile is across the twelve ICP
 * dimensions. Powers the "8/12 profile" pill on persona cards and the
 * nudge line in the editor — richer profiles produce sharper drafts and
 * outreach, so we surface (never enforce) the gap.
 */
export function personaCompleteness(profile?: PersonaProfile): PersonaCompleteness {
  const total = PROFILE_DIMENSIONS.length;
  const missing: string[] = [];
  let filled = 0;
  for (const dim of PROFILE_DIMENSIONS) {
    const v = profile?.[dim.key];
    const has = Array.isArray(v)
      ? v.some((s) => s.trim().length > 0)
      : typeof v === 'string' && v.trim().length > 0;
    if (has) filled += 1;
    else missing.push(dim.label);
  }
  return { filled, total, pct: Math.round((filled / total) * 100), missing };
}

/** Best-effort seniority band from a free-text title. */
function inferSeniority(title?: string): string | undefined {
  const t = (title ?? '').toLowerCase();
  if (!t) return undefined;
  if (/\b(ceo|cfo|coo|cto|cmo|chief|founder|owner|president)\b/.test(t)) return 'C-Suite';
  if (/\b(vp|vice president|head of)\b/.test(t)) return 'VP';
  if (/\bdirector\b/.test(t)) return 'Director';
  if (/\b(manager|lead)\b/.test(t)) return 'Manager';
  return 'Individual Contributor';
}

/** Most-common non-empty value in a list, or undefined. */
function mode(values: (string | undefined)[]): string | undefined {
  const counts = new Map<string, number>();
  for (const v of values) {
    const k = (v ?? '').trim();
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  let best: string | undefined;
  let bestN = 0;
  for (const [k, n] of counts) {
    if (n > bestN) {
      best = k;
      bestN = n;
    }
  }
  return best;
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Draft a persona grounded in the CRM: look at active prospects (falling
 * back to customers) and summarise the common title, company, and deal
 * size so the user gets a data-shaped starting point they can refine.
 */
export function synthesizePersona(people: Person[]): PersonaDraft {
  const prospects = people.filter(
    (p) =>
      p.category === 'prospect' &&
      p.stage !== 'closed-lost' &&
      p.stage !== 'closed-won',
  );
  const customers = people.filter((p) => p.category === 'customer');
  const pool = prospects.length > 0 ? prospects : customers;

  if (pool.length === 0) {
    return {
      name: 'New persona',
      archetype: 'ICP',
      summary:
        'No CRM signal to synthesize from yet. Add prospects or customers, ' +
        'then regenerate to ground this persona in real data.',
      tags: ['icp', 'synthesized'],
      profile: {},
    };
  }

  const commonTitle = mode(pool.map((p) => p.title));
  const commonCompany = mode(pool.map((p) => p.company));
  const commonSource = mode(pool.map((p) => p.source));
  const values = pool
    .map((p) => p.estimatedValue ?? p.accountValue)
    .filter((v): v is number => typeof v === 'number' && v > 0);
  const avgValue =
    values.length > 0
      ? Math.round(values.reduce((a, b) => a + b, 0) / values.length)
      : undefined;

  const sourceLabel = prospects.length > 0 ? 'prospect' : 'customer';
  const parts: string[] = [
    `Synthesized from ${pool.length} ${sourceLabel}${pool.length === 1 ? '' : 's'}.`,
  ];
  if (commonTitle) parts.push(`Most common title: ${commonTitle}.`);
  if (commonCompany) parts.push(`Representative company: ${commonCompany}.`);
  if (avgValue) parts.push(`Typical deal size ~${fmtUsd(avgValue)}.`);
  parts.push('Refine the structured profile below.');

  // Ground the structured profile in the real CRM aggregates so the
  // generated persona reads as data-shaped, not boilerplate.
  const profile: PersonaProfile = {
    role: commonTitle,
    seniority: inferSeniority(commonTitle),
    industry: undefined,
    companySize: undefined,
    goals: ['Achieve the outcome that justifies the spend'],
    painPoints: ['The current approach is slow or manual'],
    motivations: ['Measurable ROI', 'Reduced effort'],
    objections: avgValue ? [`Is roughly ${fmtUsd(avgValue)} worth it?`] : [],
    decisionCriteria: ['Proof of outcome', 'Low switching risk'],
    channels: commonSource ? [commonSource] : [],
    kpis: [],
    quote: '',
  };

  return {
    name: commonTitle ? `${commonTitle} (ICP)` : 'Synthesized persona',
    archetype: commonTitle ?? 'ICP',
    summary: parts.join(' '),
    tags: ['icp', 'synthesized', sourceLabel],
    profile,
  };
}
